import "./style.css";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useLocation, useParams, Navigate } from "react-router-dom";
import { Header, Footer, Seo } from "../../../components";
import { VideoModal } from "../../../modules/VideoModal";
import { stripHtmlToText } from "../../FAQ/faqPageUtils";
import { service, courseInquiry, enrollment } from "../../../store";
import { CourseDetailHero } from "./CourseDetailHero";
import { CourseDetailAside } from "./CourseDetailAside";
import { CourseDetailTabs } from "./CourseDetailTabs";
import { CourseDetailDescription } from "./CourseDetailDescription";
import { CourseCurriculum } from "./CourseCurriculum";
import { CourseQnaList } from "./CourseQnaList";
import { CourseMobileBar } from "./CourseMobileBar";
import {
  getCompletedLectureCount,
  getCourseTotalLectureCount,
} from "./[lecture_id]/lecturePageConfig";
import {
  sanitizeCourseDescription,
  toIso8601Duration,
  humanizeDuration,
  getCourseLectureCount,
} from "./courseDetailConfig";
import {
  COURSE_ID_TO_SLUG,
  resolveCourseId,
  courseSlugById,
} from "../courseSlugMap";
import {
  SITE_URL,
  SITE_NAME,
  OG_IMAGE,
  ORG_REF,
  canonicalUrl,
  absoluteUrl,
} from "../../../utils/seo";

export const Course = () => {
  const { course_id: routeParam } = useParams();
  const location = useLocation();

  // 라우트 파라미터는 영문 slug 또는 숫자 id일 수 있다.
  // API/스토어에는 항상 숫자 id로 접근하고, 숫자 id로 접근한 대표 과정은 slug로 301성 리다이렉트한다.
  const course_id = resolveCourseId(routeParam);
  const legacySlug = COURSE_ID_TO_SLUG[String(routeParam)];

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isCourseCompleted, setIsCourseCompleted] = useState(false);
  const [activeTab, setActiveTab] = useState("description");

  const descriptionRef = useRef(null);
  const curriculumRef = useRef(null);
  const qnaRef = useRef(null);

  const { isLoading, getService, course, clearCourse } = service((state) => ({
    isLoading: state.isLoading,
    getService: state.getService,
    clearCourse: state.clearCourse,
    course: state.course || null,
  }));

  const {
    getCourseInquiries,
    isLoading: isCourseInquiriesLoading,
    courseInquiries,
  } = courseInquiry((state) => ({
    getCourseInquiries: state.getCourseInquiries,
    isLoading: state.isLoading,
    courseInquiries: state.courseInquiries || null,
  }));

  const {
    getIsEnrolled,
    enrollment: enrollmentData,
    enrollmentProgress,
    clearEnrollment,
    refreshEnrollmentDetails,
  } = enrollment((state) => ({
    getIsEnrolled: state.getIsEnrolled,
    enrollment: state.enrollment,
    enrollmentProgress: state.enrollmentProgress,
    clearEnrollment: state.clearEnrollment,
    refreshEnrollmentDetails: state.refreshEnrollmentDetails,
  }));

  const myUserId = useMemo(() => {
    const data = localStorage.getItem("auth-storage");
    return data ? JSON.parse(data).state?.user?.userId : null;
  }, []);

  const courseDescription = useMemo(
    () => sanitizeCourseDescription(course?.description),
    [course?.description]
  );

  const seo = useMemo(() => {
    if (!course) return null;

    const plain = stripHtmlToText(course.description);
    const description = plain
      ? `${plain.slice(0, 155)}${plain.length > 155 ? "…" : ""}`
      : `${course.title} - 희망과학 심리상담센터 양형교육 과정`;

    // 대표 과정은 slug 경로를, 그 외에는 숫자 경로를 정규 URL로 사용한다.
    const slug = courseSlugById(course.id ?? course_id);
    const canonical = canonicalUrl(`/courses/${slug ?? course.id ?? course_id}`);
    const courseImage = absoluteUrl(course.thumbnail);

    const durationSource = course.total_duration || course.duration;
    const iso8601Duration = toIso8601Duration(durationSource);
    const lectureCount = getCourseLectureCount(course);
    const humanDuration = humanizeDuration(durationSource);
    const courseWorkload = lectureCount
      ? `총 ${lectureCount}개 강의${
          humanDuration ? `, 전체 교육시간 ${humanDuration}` : ""
        }`
      : undefined;

    // 실제 결제 가격(할인가가 있으면 할인가)만 구조화 데이터에 넣는다.
    const salePrice =
      typeof course.discounted_price === "number"
        ? course.discounted_price
        : typeof course.price === "number"
        ? course.price
        : undefined;

    const courseJsonLd = {
      "@context": "https://schema.org",
      "@type": "Course",
      "@id": `${canonical}#course`,
      url: canonical,
      name: course.title,
      description: plain || course.title,
      ...(courseImage ? { image: courseImage } : {}),
      inLanguage: "ko-KR",
      availableLanguage: "ko",
      ...(iso8601Duration ? { timeRequired: iso8601Duration } : {}),
      educationalCredentialAwarded: "교육 수료증",
      provider: {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: SITE_NAME,
        url: `${SITE_URL}/`,
      },
      hasCourseInstance: {
        "@type": "CourseInstance",
        courseMode: "online",
        ...(courseWorkload ? { courseWorkload } : {}),
        inLanguage: "ko-KR",
        isAccessibleForFree: false,
        ...(salePrice != null
          ? {
              offers: {
                "@type": "Offer",
                url: canonical,
                price: String(salePrice),
                priceCurrency: "KRW",
                availability: "https://schema.org/InStock",
                seller: ORG_REF,
              },
            }
          : {}),
      },
    };

    return {
      title: `${course.title} | 희망과학 양형교육`,
      description,
      canonical,
      type: "article",
      image: courseImage || OG_IMAGE,
      jsonLd: courseJsonLd,
    };
  }, [course, course_id]);

  const sectionRefs = useMemo(
    () => ({
      description: descriptionRef,
      curriculum: curriculumRef,
      qna: qnaRef,
    }),
    []
  );

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    const ref = sectionRefs[tabId];
    ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [sectionRefs]);

  useEffect(() => {
    clearEnrollment();
    clearCourse();
  }, [course_id, clearEnrollment, clearCourse]);

  useEffect(() => {
    if (course_id && myUserId) {
      getIsEnrolled(myUserId, course_id);
    }
    getService(course_id);
    getCourseInquiries(course_id);
  }, [course_id, myUserId, getIsEnrolled, getService, getCourseInquiries]);

  const refreshEnrollmentState = useCallback(async () => {
    if (!enrollmentData?.id) return;
    await refreshEnrollmentDetails(enrollmentData.id);
  }, [enrollmentData?.id, refreshEnrollmentDetails]);

  useEffect(() => {
    if (enrollmentData?.id) {
      refreshEnrollmentState();
    }
  }, [enrollmentData?.id, location.key, refreshEnrollmentState]);

  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === "visible") {
        refreshEnrollmentState();
      }
    };

    document.addEventListener("visibilitychange", handleVisible);
    return () => document.removeEventListener("visibilitychange", handleVisible);
  }, [refreshEnrollmentState]);

  useEffect(() => {
    if (!course || !enrollmentData) return;

    const total = getCourseTotalLectureCount(course, enrollmentData);
    const completed = getCompletedLectureCount(
      enrollmentProgress,
      enrollmentData
    );

    setIsCourseCompleted(
      Boolean(enrollmentData.is_completed) ||
        (total > 0 && completed >= total)
    );
  }, [enrollmentProgress, course, enrollmentData]);

  const showMobileBar = !isLoading && course && !enrollmentData;

  // 숫자 id로 접근한 대표 과정은 영문 slug 정규 URL로 즉시 이동시킨다.
  // (서버측 301은 Amplify 콘솔 리다이렉트로 별도 처리 — AMPLIFY_REDIRECTS.md 참고)
  if (legacySlug) {
    return <Navigate to={`/courses/${legacySlug}`} replace />;
  }

  return (
    <>
      {seo && <Seo {...seo} />}
      <div className="course-detail-page-shell">
        <Header variant="solid" />
        <main
          className={`course-detail-page${
            showMobileBar ? " course-detail-page--with-mobile-bar" : ""
          }`}
        >
          {isLoading ? (
            <p className="course-detail-page__loading">불러오는 중...</p>
          ) : (
            <>
              <div className="course-detail-layout">
                <div className="course-detail-hero-band" aria-hidden="true" />
                <CourseDetailHero
                  course={course}
                  enrollmentData={enrollmentData}
                  isCourseCompleted={isCourseCompleted}
                  myUserId={myUserId}
                  onPreviewClick={() => setIsPreviewOpen(true)}
                />

                <aside
                  className="course-detail-layout__sidebar"
                  aria-label="수강 정보"
                >
                  <CourseDetailAside
                    course={course}
                    enrollmentData={enrollmentData}
                    isCourseCompleted={isCourseCompleted}
                    myUserId={myUserId}
                  />
                </aside>

                <div className="course-detail-body">
                  <CourseDetailTabs
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                  />

                  <div className="course-detail-content">
                    <CourseDetailDescription
                      description={courseDescription}
                      sectionRef={descriptionRef}
                    />
                    <CourseCurriculum
                      courseId={course?.id ?? course_id}
                      sections={course?.sections}
                      enrollmentData={enrollmentData}
                      enrollmentProgress={enrollmentProgress}
                      sectionRef={curriculumRef}
                    />
                    <CourseQnaList
                      courseId={course?.id ?? course_id}
                      inquiries={courseInquiries ?? []}
                      isLoading={isCourseInquiriesLoading}
                      sectionRef={qnaRef}
                    />
                  </div>
                </div>
              </div>

              <CourseMobileBar
                course={course}
                myUserId={myUserId}
                visible={showMobileBar}
              />
            </>
          )}

          <VideoModal
            isOpen={isPreviewOpen}
            videoSrc={course?.sections?.[0]?.lectures?.[0]?.video_url}
            onClose={() => setIsPreviewOpen(false)}
          />
        </main>
      </div>
      <Footer />
    </>
  );
};
